// Stage 1, two-way Outlook (DESIGN-outlook-two-way-stage1-2026-10-26 §2a): the settings form, the user's sync
// status lines and the line on a task or event. The words are the design's.

const DESCRIPTION_HELP = "Not recommended: Outlook rewrites the description's formatting, and the description carries the link back to the CRM record."
// The user form's "Microsoft To Do (tasks)" switch (Admin only, as Create Outlook Event).
export const TODO_HELP = "CRM tasks (not Calls or Meetings) go to this person's To Do list 'Synergy CRM', which also shows in Outlook Tasks and My Day; ticking or editing them there updates the CRM."
export const OFF_HELP = 'Changes in Outlook are ignored and put back from the CRM.'

// What Outlook may change, as the settings page groups it. Keep the keys in step with the backend (the PUT
// answers 400 to any other). `later`: the backend takes the switch but it has no effect yet (be7 4b10dfc), so the
// page hides it and the PUT never sends it; the saved value stays as it is.
const ALL_SYNC_GROUPS = [
  {
    key: 'calendar',
    header: 'Meetings & Calls',
    fields: [
      { key: 'calStartEnd', label: 'Moved (start/end; the reminder moves too)' },
      { key: 'calSubject', label: 'Subject' },
      { key: 'calLocation', label: 'Location', later: true },
      { key: 'calCancelled', label: 'Deleted or cancelled → cancels the CRM activity' },
      { key: 'calBody', label: 'Description', help: DESCRIPTION_HELP, later: true }
    ]
  },
  {
    key: 'todo',
    header: 'Tasks',
    fields: [
      { key: 'taskTitle', label: 'Title' },
      { key: 'taskDueDate', label: 'Due date' },
      { key: 'taskCompleted', label: 'Ticked / unticked → completes / reopens the CRM task' },
      { key: 'taskBody', label: 'Description', help: DESCRIPTION_HELP, later: true }
    ]
  },
  {
    // Not a change Outlook makes: the quote note is always written (Mohammed 28/10); this only decides the email.
    key: 'quotes',
    header: 'Quotes',
    fields: [
      {
        key: 'quoteNoteEmail',
        label: 'Email the quote note when Outlook or To Do completes or cancels a task / event on a quote',
        help: 'The note is always written on the quote; this only decides whether it is also emailed.',
        offHelp: ''
      }
    ]
  }
]

export const SYNC_GROUPS = ALL_SYNC_GROUPS.map(g => ({ ...g, fields: g.fields.filter(f => !f.later) }))

// The switches the page shows and saves.
export const SYNC_KEYS = SYNC_GROUPS.reduce((keys, g) => keys.concat(g.fields.map(f => f.key)), [])

// The backend's defaults: everything on except the two descriptions and the quote note email.
const DEFAULTS = { calBody: false, taskBody: false, quoteNoteEmail: false }

/** The saved settings as the form edits them: the shown switches, each true or false. */
export function toForm(saved) {
  const s = saved || {}
  const form = {}
  SYNC_KEYS.forEach(k => { form[k] = typeof s[k] === 'boolean' ? s[k] : DEFAULTS[k] !== false })
  return form
}

/** The PUT body: the shown switches only (never tieSeconds, which the PUT refuses, nor a `later` one). */
export function toBody(form) {
  const body = {}
  SYNC_KEYS.forEach(k => { body[k] = form[k] === true })
  return body
}

export function tieHelp(tieSeconds) {
  const n = Number(tieSeconds) > 0 ? Number(tieSeconds) : 60
  return `When both sides change within ${n} seconds of each other, the CRM's change wins.`
}

// The backend skips a user's calendar or To Do until the next day after this many failed checks in a row
// (OutlookSyncState.PAUSE_AFTER_FAILURES).
export const PAUSE_AFTER_FAILURES = 5

/** One status line for Calendar or To Do on the user form: `{ label, text, problem }`. */
export function channelLine(label, c) {
  if (!c || !c.on) return { label, text: 'Not switched on', problem: '' }
  const checked = c.lastSyncAt ? 'Last checked ' + c.lastSyncAt : 'Not checked yet'
  const linked = (Number(c.linkedItems) || 0) + ' linked'
  const problems = []
  if ((Number(c.failures) || 0) >= PAUSE_AFTER_FAILURES) problems.push(`Paused until tomorrow after ${PAUSE_AFTER_FAILURES} failed checks.`)
  if (c.lastError) problems.push('Problem: ' + c.lastError)
  return { label, text: checked + ' · ' + linked, problem: problems.join(' ') }
}

/** The user form's lines; `[]` when there is no status to show. */
export function userStatusLines(status) {
  if (!status) return []
  const lines = [channelLine('Calendar', status.calendar), channelLine('To Do', status.todo)]
  if (status.serverSync === false) {
    lines.push({ label: '', text: '', problem: 'Changes made in Outlook are not being read on this server' })
  }
  return lines
}

/** The task or event line: `null` for no line, else `{ text, error }`. */
export function activityLine(sync) {
  if (!sync || !sync.status || !sync.message) return null
  return { text: sync.message, error: sync.status === 'ERROR' }
}
